import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Message, Radio, Space } from '@bedrock/components';
import FilterBar, {
  SupplierFilterEnum,
  ViewCurrency,
} from '../components/FilterBar';
import ExpertView from '../components/ExpertView';
import SupplierView from '../components/SupplierView';
import styles from './index.less';
import classNames from 'classnames/bind';
import { useDemandOptions } from '#features/useDemandOptions';
import { useImportFileModal } from '#features/importFileModal';
import {
  CurrencyEnum,
  demandDetailHisRound,
  DemandDetailsResDtoDemandSupplierDto,
  DemandSourceCustomInspectionVo,
  DemandSourceSupplierKolVo,
} from '#services/api';
import { useMemoizedFn } from 'ahooks';
import { ProTablePlusFormRef } from '#components/ProTablePlusForm';
import SearchFilterBar from '../components/SearchFilterBar';
import { formatData, sortBySupplier } from './format';
import { isBlank } from '@bedrock/utils';
import { FullScreenButton } from '@mpc/components';
import RejectSuppliers from '../components/RejectSuppliers';
import { useChangeRate } from '#hooks/useChangeRate';
import { KOLDemandCategory } from './type';
import { uniqBy, omit } from 'lodash';
import { useScreenAdapterContext } from '@bedrock/eed-react';
import { useFilterModel } from './useFilterConfig';
import { Download, SignOut } from '@bedrock/icons-react';

const cx = classNames.bind(styles);

interface KOLTableData {
  demand_source_supplier_kol_vos: DemandSourceSupplierKolVo[];
  demand_suppliers: DemandDetailsResDtoDemandSupplierDto[];
}

interface KOLTableProps {
  /** 数据变化回调 */
  value?: KOLTableData;
  onChange?: (data: any) => void;
  onDataChange?: (data: any[]) => void;
  setIsFullscreen?: (isFullscreen: boolean) => void;
  showPlannerAuditColumn?: { editable?: boolean } | false;
  showMarketingAuditColumn?: { editable?: boolean } | false;
  turnsNumber?: number;
  demandId?: number;
  demandState?: string;
  showSupplierTag?: boolean;
  defaultTab?: 'expert' | 'supplier';
  /**
   * 审核场景：传入后组件内部提供「导入 / 导出全部数据」（导出带 audit 参数，导入按场景回填对应审核列）
   * plan = 策划确认报价，marketing = 需求方确认报价；不传则无导入导出（如只读详情页）
   */
  auditScene?: 'plan' | 'marketing';
  /** 工具条左侧插槽：KOL 智能推荐入口（业务页传入，不传则不渲染） */
  recommendTrigger?: React.ReactNode;
  // 类型：直播单、图文单
  category?: KOLDemandCategory;
  /** 定制排查列表 */
  customInspections?: DemandSourceCustomInspectionVo[];
  /** 定制排查ID列表 */
  customInspectionIdList?: number[];
}

export type KOLTableRef = ProTablePlusFormRef & {
  /** 按当前选中轮次导出达人数据 */
  exportAll: () => void;
};

const TAB_OPTIONS = [
  { name: '达人视图', value: 'expert' },
  { name: '供应商视图', value: 'supplier' },
];

const omitFields = ['__table_row_key__', 'parent', 'children'];

/** 度量标签筛选「标签组」分组头 value 前缀（分组头不可选中，仅作分隔展示） */
const METRIC_TAG_GROUP_HEADER_PREFIX = '__metric_tag_group__';

/**
 * 从列表数据的度量标签（metric_tags / MetricTagStorage）按「标签组-标签」拼接筛选选项
 * 标签组作为不可选分组头（type: 'header'），标签为可选叶子项，选项值取标签名
 * @param kolVos 供应商提报达人列表（查询结果）
 */
const buildMetricTagOptions = (kolVos: DemandSourceSupplierKolVo[]): any[] => {
  const groupMap = new Map<string, Set<string>>();
  (kolVos || []).forEach((kol) => {
    ((kol as any)?.metric_tags?.xingtu_tag_info || []).forEach((info: any) => {
      const category = info?.tag_category;
      (info?.xingtu_tag_list || []).forEach((tag: string) => {
        if (!category || !tag) {
          return;
        }
        if (!groupMap.has(category)) {
          groupMap.set(category, new Set());
        }
        groupMap.get(category)?.add(tag);
      });
    });
  });
  const options: any[] = [];
  groupMap.forEach((tags, category) => {
    options.push({
      name: category,
      value: `${METRIC_TAG_GROUP_HEADER_PREFIX}${category}`,
      type: 'header',
    });
    Array.from(tags).forEach((tag) => {
      options.push({ name: tag, value: tag });
    });
  });
  return options;
};

/**
 * 策划预选达人表格组件
 * 支持达人视图和供应商视图切换
 */
const KOLTable: React.FC<KOLTableProps> = forwardRef<
  KOLTableRef,
  KOLTableProps
>((props, ref) => {
  const {
    value = {
      demand_source_supplier_kol_vos: [],
      demand_suppliers: [],
    },
    onChange,
    showPlannerAuditColumn = false,
    showMarketingAuditColumn = false,
    turnsNumber = 1,
    demandId,
    demandState,
    showSupplierTag = true,
    defaultTab = 'expert',
    auditScene,
    recommendTrigger,
    category = 'live',
    customInspections,
    customInspectionIdList = [],
  } = props;


  /** 达人视图表格引用 */
  const expertTableRef = useRef<any>(null);
  /** 供应商视图表格引用 */
  const supplierTableRef = useRef<any>(null);
  /** 搜索筛选栏引用 */
  const searchFilterBarRef = useRef<any>(null);
  /** 当前激活的tab */
  const [activeTab, setActiveTab] = useState<'expert' | 'supplier'>(defaultTab);
  /** 当前数据 */
  const [data, setData] = useState(() =>
    activeTab === 'expert'
      ? formatData(value.demand_source_supplier_kol_vos, customInspectionIdList)
      : sortBySupplier(
          value.demand_source_supplier_kol_vos,
          customInspectionIdList
        )
  );
  const { viewBase } = useScreenAdapterContext();
  const dataRef = useRef(data);
  /** 是否显示加载中 */
  const [loading, setLoading] = useState(false);
  /** 历史轮次数据缓存 */
  const historyRoundDataCache = useRef<{
    [key: number]: KOLTableData;
  }>({});

  /** 拒单供应商数据 */
  const [rejectSupplierData, setRejectSupplierData] = useState<
    DemandDetailsResDtoDemandSupplierDto[]
  >(() => value.demand_suppliers || []);

  const { changeRateList } = useChangeRate();

  useEffect(() => {
    setRejectSupplierData(value.demand_suppliers || []);
  }, [JSON.stringify(value.demand_suppliers)]);

  /** 轮次选项 */
  const turnsOptions = useMemo(() => {
    return Array.from({ length: turnsNumber ?? 1 }, (_, index) => ({
      name: `${index + 1}轮`,
      value: index + 1,
    })).reverse();
  }, [value]);

  // 筛选状态
  const [viewCurrency, setViewCurrency] = useState<ViewCurrency>('original');
  const [currentRound, setCurrentRound] = useState<number>(
    turnsOptions[0].value
  );

  const { optionsMap } = useDemandOptions({
    keys: ['main_platform'],
  });
  const customInspectionOptions = useMemo(() => {
    return [{ name: '指定达人', value: 'by_plan' }].concat(
      (customInspections || []).map((item, index) => {
        return {
          name: `定制排查${index + 1}-${item.main_platform}`,
          value: item.id,
        };
      }) as any[]
    );
  }, [customInspections]);

  /**
   * 度量标签筛选选项：从当前列表查询数据按「标签组-标签」拼接（非写死），
   * 列表数据变化时自动更新
   */
  const metricTagOptions = useMemo(
    () => buildMetricTagOptions(value.demand_source_supplier_kol_vos),
    [JSON.stringify(value.demand_source_supplier_kol_vos)]
  );

  /** 最终视图币种 */
  const finalViewCurrency = useMemo(() => {
    return viewCurrency === 'original'
      ? undefined
      : (viewCurrency.toUpperCase() as CurrencyEnum);
  }, [viewCurrency]);

  /** 是否是最新一轮次 */
  const isLatestRound = useMemo(() => {
    return currentRound === turnsOptions[0].value;
  }, [currentRound]);

  const finalShowPlannerAuditColumn = useMemo(() => {
    if (showPlannerAuditColumn && showPlannerAuditColumn?.editable) {
      return isLatestRound ? showPlannerAuditColumn : false;
    }
    return showPlannerAuditColumn;
  }, [isLatestRound, showPlannerAuditColumn]);

  const finalShowMarketingAuditColumn = useMemo(() => {
    if (showMarketingAuditColumn && showMarketingAuditColumn?.editable) {
      return isLatestRound ? showMarketingAuditColumn : false;
    }
    return showMarketingAuditColumn;
  }, [isLatestRound, showMarketingAuditColumn]);

  /**
   * 当前审核场景是否可导入：与场景审核列的「可编辑 + 最新轮次」同源。
   * 只读态导入会绕过只读列约束（值随保存/提交落库），历史轮次导入的行 id 匹配不到最新轮次数据，
   * 两种情况都不给导入入口。
   */
  const auditSceneImportable =
    auditScene === 'marketing'
      ? Boolean(
          finalShowMarketingAuditColumn && finalShowMarketingAuditColumn.editable
        )
      : Boolean(
          finalShowPlannerAuditColumn && finalShowPlannerAuditColumn.editable
        );

  // 使用筛选模型 Hook
  const {
    filterData,
    setFilterData,
    config: filterConfig,
    resetFilter,
    handleDataFilter,
  } = useFilterModel(
    {
      activeTab,
      category,
      demandState,
      changeRateList,
      viewCurrency,
      optionsMap,
      customInspectionOptions,
      metricTagOptions,
      showPlannerAuditColumn: finalShowPlannerAuditColumn,
      customInspectionIdList,
    },
    searchFilterBarRef
  );

  /** 全屏状态 */
  const wrapperRef = useRef(null);

  /** 重置筛选并重置轮次 */
  const handleResetFilter = useMemoizedFn(async () => {
    setCurrentRound(turnsOptions[0].value);
    await resetFilter();
  });

  /** 供应商筛选变化回调 */
  const handleSupplierFilterChange = useMemoizedFn(
    (filter: SupplierFilterEnum) => {
      setFilterData((prev) => ({
        ...prev,
        only_lowest_price: filter === SupplierFilterEnum.LOWEST,
      }));
    }
  );

  /** 导出当前轮次数据；审核场景附带 audit 参数（后端按场景生成「策划预筛选/策划备注」或「需求方确认/需求方备注」表头） */
  const handleExport = useMemoizedFn(() => {
    if (!demandId) {
      return;
    }
    const auditParam = auditScene ? `&audit=${auditScene}` : '';
    if (currentRound !== turnsOptions[0].value) {
      window.open(
        `/api/v2/java_proxy/demands/export_detail_his_round_kol?demand_id=${demandId}&turns_number=${currentRound}${auditParam}`
      );
      return;
    }
    window.open(
      `/api/v2/java_proxy/demands/export_detail_kol?id=${demandId}${auditParam}`
    );
  });

  /**
   * 导入结果合并：
   * - 仅回填当前审核场景的两列（plan: 策划预筛选/策划备注；marketing: 需求方确认/需求方备注）
   * - 行出现在导入结果中即代表「两列以文件为准」：文件里为空 = 清空
   *   （后端对空值/「未确认」返回 null，且 null 字段可能不出现在响应 JSON 中，故用 ?? null 而非 !== undefined）
   * - 已提交需求方（submit_demand）的行不参与覆盖
   */
  const mergeImportedRows = useMemoizedFn(
    (list: DemandSourceSupplierKolVo[]) => {
      const patchMap = new Map<number, DemandSourceSupplierKolVo>(
        list.map((v) => [v.id, v])
      );
      const isMarketing = auditScene === 'marketing';
      return value.demand_source_supplier_kol_vos.map(
        (item: DemandSourceSupplierKolVo) => {
          const patch = patchMap.get(item.id);
          if (!patch || (isMarketing && item.submit_demand)) {
            return item;
          }
          if (isMarketing) {
            return {
              ...item,
              principal_confirm: patch.principal_confirm ?? null,
              principal_remark: patch.principal_remark ?? null,
            };
          }
          return {
            ...item,
            /** 策划预筛选为必填（后端对空值直接报错），仅在文件给了值时覆盖 */
            ...(patch.plan_suggestion !== undefined
              ? { plan_suggestion: patch.plan_suggestion }
              : {}),
            plan_remark: patch.plan_remark ?? null,
          };
        }
      );
    }
  );

  const { openImportFileModal, closeImportFileModal } = useImportFileModal({});

  /** 审核场景的导入：合并后同时回填表单状态（onChange）与表格数据（tableRef，避免受控回流不生效） */
  const handleOpenImport = useMemoizedFn(() => {
    if (!demandId || !auditScene || !auditSceneImportable) {
      return;
    }
    const isMarketing = auditScene === 'marketing';
    openImportFileModal({
      importTitle: '导入',
      importType: `demand_source_supplier_audit_kol${demandId}`,
      importIsMemory: false,
      fileType: '.xls',
      acceptText: '支持xls格式',
      showSuccessToast: false,
      maskClosable: false,
      scene: auditScene,
      customDownloadRender: () => (
        <div>
          <Button type="link" icon={<Download />} onClick={handleExport}>
            导出当前数据
          </Button>
          <div className="download-span fs-12 m-l-4">
            (请使用系统导出的表格作为导入模板，仅填写
            {isMarketing
              ? '「需求方确认」「需求方备注」'
              : '「策划预筛选」「策划备注」'}
            两列，不要改动或删除其他列，否则将导致导入失败。)
          </div>
        </div>
      ),
      uploadFinishedCallback: (res: any) => {
        if (res?.data?.fail_count > 0) {
          return;
        }
        const list = res?.data?.data;
        if (!Array.isArray(list) || !list.length) {
          return;
        }
        const next = mergeImportedRows(list);
        /** 1) 同步表单状态（保存/提交读取的就是这份） */
        onChange?.({ ...value, demand_source_supplier_kol_vos: next });
        /** 2) 走组件内既有筛选/格式化管线刷新当前视图数据（保留筛选条件） */
        handleDataFilter(next, dataRef, setData, setLoading);
        /** 3) 命令式写回两个视图的表格数据（ProTablePlusForm 非完全受控，不能只依赖 value 回流） */
        const expertRows =
          activeTab === 'expert'
            ? dataRef.current
            : formatData(next, customInspectionIdList);
        const supplierRows =
          activeTab === 'supplier'
            ? dataRef.current
            : sortBySupplier(next, customInspectionIdList);
        // 注意：bedrock 的 updateData 函数式入参是「无返回值」实现（ProTable/table.js: updateData ->
        // setData(function(draft){ arg(draft); })），传函数返回新数组不会生效；必须直接传数组（内部走 setInitData）
        expertTableRef.current?.updateData?.(expertRows);
        supplierTableRef.current?.updateData?.(supplierRows);

        const importedIds = new Set(
          list.map((v: DemandSourceSupplierKolVo) => v.id)
        );
        const skippedCount = isMarketing
          ? value.demand_source_supplier_kol_vos.filter(
              (item: DemandSourceSupplierKolVo) =>
                item.submit_demand && importedIds.has(item.id)
            ).length
          : 0;
        Message.success(
          skippedCount
            ? `导入成功 ${list.length - skippedCount} 条，${skippedCount} 条已提交的达人保持原值`
            : `导入成功 ${list.length} 条`
        );
        closeImportFileModal();
      },
    });
  });

  useImperativeHandle(ref, () => {
    if (expertTableRef.current) {
      return {
        ...expertTableRef.current,
        exportAll: handleExport,
        validateFields: async () => {
          await handleResetFilter();
          const promiseList = [
            expertTableRef.current?.validateFieldsAndScroll?.(),
            supplierTableRef.current?.validateFieldsAndScroll?.(),
          ];
          if (
            typeof showMarketingAuditColumn === 'object' &&
            showMarketingAuditColumn?.editable
          ) {
            promiseList.push(
              new Promise((resolve, reject) => {
                value.demand_source_supplier_kol_vos.some(
                  (item) => !!item.principal_confirm
                )
                  ? resolve(true)
                  : reject('至少有一项需求方确认不能为空');
              })
            );
          }

          return Promise.all(promiseList);
        },
      };
    }
    return {} as KOLTableRef;
  });

  /** 筛选数据变化回调 */
  useEffect(() => {
    // 异步执行筛选，避免阻塞
    setTimeout(() => {
      if (currentRound === turnsOptions[0].value) {
        handleDataFilter(
          value.demand_source_supplier_kol_vos,
          dataRef,
          setData,
          setLoading
        );
      } else {
        const roundData = historyRoundDataCache.current[currentRound];
        if (roundData) {
          handleDataFilter(
            roundData.demand_source_supplier_kol_vos,
            dataRef,
            setData,
            setLoading
          );
        }
      }
    }, 0);
  }, [
    JSON.stringify(filterData),
    activeTab,
    JSON.stringify(value.demand_source_supplier_kol_vos),
    viewCurrency,
    currentRound, // 添加 currentRound 依赖
  ]);

  /**
   * 数据变化回调
   * @param data 数据
   * @param changeData 变化数据
   * @param updateByKey 更新键
   */
  const handleDataChange = useMemoizedFn(
    (data: any[], changeData: any, updateByKey: string = 'id') => {
      const updateMap = {};
      changeData.updateData.forEach((item: any) => {
        updateMap[item[updateByKey]] = item;
      });
      if (isBlank(updateMap)) {
        return;
      }
      const newValue = value.demand_source_supplier_kol_vos.map((item: any) => {
        if (updateMap[item[updateByKey]]) {
          return { ...item, ...updateMap[item[updateByKey]] };
        }
        return item;
      });

      onChange?.({ ...value, demand_source_supplier_kol_vos: newValue });

      const newData = data.map((item: any) => {
        let res = item;
        if (updateMap[item[updateByKey]]) {
          res = {
            ...item,
            ...updateMap[item[updateByKey]],
          };
        }
        return omit(res, omitFields);
      });

      // setData(newData);
      dataRef.current = newData;
    }
  );

  // 达人视图数据变化回调
  const handleExpertDataChange = useMemoizedFn(
    (data: any[], changeData: any) => {
      const updateData = changeData.updateData.map((item: any) => {
        if (showPlannerAuditColumn && showPlannerAuditColumn?.editable) {
          /** 策划预筛选/策划备注只需要更新 plan_suggestion、plan_remark*/
          return {
            index: item.index,
            plan_suggestion: item.plan_suggestion,
            plan_remark: item.plan_remark,
          };
        } else if (
          showMarketingAuditColumn &&
          showMarketingAuditColumn?.editable
        ) {
          /** 需求方确认/需求方备注只需要更新 principal_confirm、principal_remark*/
          return {
            index: item.index,
            principal_confirm: item.principal_confirm,
            principal_remark: item.principal_remark,
          };
        }
        return item;
      });

      handleDataChange(
        data,
        { ...changeData, updateData: updateData },
        'index'
      );
    }
  );

  const onRoundChange = useMemoizedFn(async (round: number) => {
    if (!demandId) return;
    await handleResetFilter();
    setCurrentRound(round);
    if (round === turnsOptions[0].value) {
      setRejectSupplierData(value.demand_suppliers);
      handleDataFilter(
        value.demand_source_supplier_kol_vos,
        dataRef,
        setData,
        setLoading
      );
    } else if (historyRoundDataCache.current[round]) {
      setRejectSupplierData(
        historyRoundDataCache.current[round].demand_suppliers
      );
      handleDataFilter(
        historyRoundDataCache.current[round].demand_source_supplier_kol_vos,
        dataRef,
        setData,
        setLoading
      );
    } else {
      try {
        setLoading(true);
        const res = await demandDetailHisRound({
          demand_id: demandId,
          turns_number: round,
        });
        setRejectSupplierData(res.data.demand_suppliers);
        historyRoundDataCache.current[round] = {
          demand_source_supplier_kol_vos:
            res.data.demand_source_detail_vo.demand_source_supplier_kol_vos,
          demand_suppliers: res.data.demand_suppliers,
        };
        handleDataFilter(
          historyRoundDataCache.current[round].demand_source_supplier_kol_vos,
          dataRef,
          setData,
          setLoading
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleTabChange = useMemoizedFn((tab: 'expert' | 'supplier') => {
    setLoading(true);
    setActiveTab(tab);
    // 切换tab时，清空对应不支持的筛选项
    if (searchFilterBarRef.current) {
      searchFilterBarRef.current.setFieldValue(
        tab === 'expert' ? 'show_only_recommend_rollback' : 'plan_suggestion',
        undefined
      );
    }
  });

  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullChange = useMemoizedFn((isFullscreen: boolean) => {
    setIsFullscreen(isFullscreen);
    props.setIsFullscreen?.(isFullscreen);
  });

  const scroll = {
    x: 'max-content',
    y: isFullscreen ? window.innerHeight - 190 * viewBase : 560,
  };

  /** 导出按钮：审核页挂在审核工具条内，非审核页保持在轮次切换行（与历史位置一致，不做能力回退） */
  const exportButton = (
    <Button
      type="checked-neutral"
      icon={<SignOut />}
      onClick={handleExport}
    >
      导出全部数据
    </Button>
  );

  return (
    <Space direction="vertical" size={12} className="w-full">
      <div ref={wrapperRef} className="w-full">
        {/* 审核工具条：左侧智能推荐入口（业务页传入），右侧导入/导出（仅达人视图） */}
        {(auditScene || recommendTrigger) && (
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">{recommendTrigger}</div>
            <div className="flex items-center gap-2">
              {auditSceneImportable && activeTab === 'expert' && (
                <Button
                  type="checked-neutral"
                  icon={<Download />}
                  onClick={handleOpenImport}
                >
                  导入
                </Button>
              )}
              {auditScene && activeTab === 'expert' && exportButton}
            </div>
          </div>
        )}
        {/* Tabs */}
        <div className={cx('kol-table-tabs')}>
          <div className="flex items-center gap-2">
            <Radio.Group
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
            >
              {TAB_OPTIONS.map((option) => {
                return (
                  <Radio.Button
                    checked={option.value === activeTab}
                    key={option.value}
                    value={option.value}
                  >
                    <div className="inline-flex items-center gap-1">
                      <span>{option.name}</span>
                    </div>
                  </Radio.Button>
                );
              })}
            </Radio.Group>
            {!auditScene && activeTab === 'expert' && exportButton}
          </div>

          {/* 筛选栏 */}
          <FilterBar
            roundOptions={turnsOptions}
            currentRound={currentRound}
            onRoundChange={onRoundChange}
            viewCurrency={viewCurrency}
            onViewCurrencyChange={setViewCurrency}
            supplierFilter={
              filterData.only_lowest_price
                ? SupplierFilterEnum.LOWEST
                : SupplierFilterEnum.SHOW_ALL
            }
            onSupplierFilterChange={handleSupplierFilterChange}
            showSupplierFilter={activeTab === 'expert'}
            rightSlot={
              <FullScreenButton
                fullRef={wrapperRef}
                handleFullChange={handleFullChange}
              />
            }
          />
        </div>

        {/* 搜索筛选栏 */}
        <SearchFilterBar
          key={activeTab}
          ref={searchFilterBarRef}
          filterConfig={filterConfig}
          disableAddButton={activeTab === 'supplier'}
          onChange={(params) =>
            setFilterData((prev) => ({ ...prev, ...params }))
          }
        />

        {/* 表格内容 */}
        <div className="kol-table-content">
          <ExpertView
            className={activeTab !== 'expert' && 'hidden'}
            showPlannerAuditColumn={finalShowPlannerAuditColumn}
            showMarketingAuditColumn={finalShowMarketingAuditColumn}
            ref={expertTableRef}
            onDataChange={handleExpertDataChange}
            data={data}
            viewCurrency={finalViewCurrency}
            loading={loading}
            category={category}
            isFullscreen={isFullscreen}
            scroll={scroll}
          />
          <SupplierView
            className={activeTab !== 'supplier' && 'hidden'}
            data={data}
            ref={supplierTableRef}
            onDataChange={handleDataChange}
            viewCurrency={finalViewCurrency}
            loading={loading}
            category={category}
            isFullscreen={isFullscreen}
            scroll={scroll}
          />
        </div>
      </div>
      <RejectSuppliers data={rejectSupplierData} showTag={showSupplierTag} />
    </Space>
  );
});

export default KOLTable;
